/* @flow */
import * as React from 'react';
import { translate } from 'react-i18next';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import theme from './ChangePassword.scss';
import {
    personnelClientIdSelector,
    personnelIdSelector,
    isGlobalPersonnelSelector,
    defaultClientSelector,
    sessionSelector
} from 'common/redux/selectors/sessionSelector';
import { Session } from 'common/models/internal/Session';

import { dashboardColors } from 'common/enums/ColorEnum';
import { showToast } from 'common/redux/actions/toast';
import {
    withRouter,
    type RouterHistory,
    type Location
} from 'common/components/core/router';
import { PasswordExpiryMessage } from 'common/components/core/password';
import type { TFunction } from 'react-i18next';
import { isChangePasswordActive as isChangePasswordActiveSelector } from 'common/redux/selectors/queryParamsSelectors';
import ChangePasswordBase from './ChangePasswordBase';
import { HelpTooltip } from 'common/components/core/help';
import { Button, ButtonTypes } from 'common/components/core/button';
import { Loader } from 'common/components/core/loader';
import { ToastTypes } from 'common/components/core/toast/Toast';
import { Columns, Flex, Stack } from '@locus-taxy/neo-ui';
import settingsTestIds from 'common/tests/mySettings/settings.json';
type State = {
    status: ?'TIMEOUT',
    showChangePassword: boolean
};

type Props = {
    t: TFunction,
    location: Location,
    isChangePasswordInUrl: boolean,
    history: RouterHistory,
    personnelClientId: string,
    personnelId: string,
    isGlobalPersonnel: string,
    sessionDataClientId: string,
    session: ?Session,
    showToast: (
        {
            message: string,
            subText: string,
            type: string
        },
        { clientId: string, personnelId: string }
    ) => void,
    ...OwnProps
};
type OwnProps = {};

class SettingsChangePassword extends React.PureComponent<Props, State> {
    state = {
        status: null,
        showChangePassword: Boolean(this.props.isChangePasswordInUrl)
    };

    redirectToHome = () => {
        const { history } = this.props;

        history.push({
            pathname: '/'
        });
    };

    renderExpiryMessage({ session }) {
        if (
            session &&
            session.passwordMetadata.expiryDate &&
            (session.isPasswordExpiringInNext7Days() ||
                session.isPasswordExpired())
        ) {
            return (
                <PasswordExpiryMessage
                    className={theme.expiryMessage}
                    expiryDate={session.passwordMetadata.expiryDate}
                />
            );
        }
        return null;
    }

    handleShowChangePassword = () => {
        this.setState({ showChangePassword: true });
    };

    handleHideChangePassword = () => {
        this.setState({ showChangePassword: false });
    };

    renderShowChangePasswordButton = () => {
        const { t, isGlobalPersonnel, sessionDataClientId } = this.props;
        const isDisabled = !!(
            isGlobalPersonnel && sessionDataClientId !== 'mara'
        );
        return (
            <Flex alignItems="flex-end">
                <Columns align="center">
                    <Button
                        uppercase
                        semibold
                        ripple={false}
                        color={dashboardColors.darkSkyBlue.name}
                        disabled={isDisabled}
                        onClick={this.handleShowChangePassword}
                        className={theme.headerButton}
                        theme={theme}
                        buttonType={ButtonTypes.Flat}
                        testId={settingsTestIds.changePassword}
                    >
                        {t('changePassword')}
                    </Button>
                    {isDisabled ? (
                        <HelpTooltip
                            tooltipText={t('errorMessage.maraPersonnel')}
                        />
                    ) : null}
                </Columns>
            </Flex>
        );
    };

    onSucess = () => {
        const {
            showToast,
            t,
            personnelClientId: clientId,
            personnelId
        } = this.props;
        this.setState({ status: 'TIMEOUT' });

        const showToastFor = 4000;

        showToast(
            {
                message: t('sucessLabel'),
                subText: t('successSubText'),
                type: ToastTypes.Success,
                timeout: showToastFor
            },
            { clientId, personnelId }
        );

        /**
         * NOTE: adding 300msec to make sure success toast is closed
         * kind of hack. Currently if toast is not closed before
         * logout then it will show up again after login.
         */
        const timeToWaitForSuccessToastToHide = showToastFor + 300;
        setTimeout(this.redirectToHome, timeToWaitForSuccessToastToHide);
    };

    onCancel = () => {
        const { history, location, isChangePasswordInUrl } = this.props;

        this.setState({
            showChangePassword: false
        });

        if (isChangePasswordInUrl) {
            history.push({
                pathname: location.pathname,
                search: ''
            });
        }
    };

    render() {
        const { personnelId, personnelClientId, session } = this.props;
        const { state } = this;

        if (this.state.status === 'TIMEOUT') {
            return <Loader solid absolute active zIndex={1002} />;
        }
        return (
            <Stack space="small">
                {this.renderExpiryMessage({ session })}
                {state.showChangePassword ? (
                    <ChangePasswordBase
                        onCancel={this.onCancel}
                        username={`${personnelClientId}/${personnelId}`}
                        onUpdatePasswordSuccess={this.onSucess}
                        showTitle
                    />
                ) : (
                    this.renderShowChangePasswordButton()
                )}
            </Stack>
        );
    }
}

const mapStateToProps = (state: Object, ownProps: Object) => {
    const sessionDataClientId = defaultClientSelector(state);
    const session = sessionSelector(state);
    const isGlobalPersonnel = isGlobalPersonnelSelector(state, ownProps);
    const isChangePasswordInUrl = isChangePasswordActiveSelector(
        state,
        ownProps
    );
    const personnelClientId = personnelClientIdSelector(state);
    const personnelId = personnelIdSelector(state);

    return {
        ...ownProps,
        session,
        isChangePasswordInUrl,
        personnelClientId,
        personnelId,
        isGlobalPersonnel,
        sessionDataClientId
    };
};

const mapDispatchToProps = {
    showToast
};

export default (compose(
    withRouter,
    connect(mapStateToProps, mapDispatchToProps),
    translate('settings')
)(SettingsChangePassword): React.AbstractComponent<OwnProps>);
